/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable no-use-before-define */
import React, { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { Box, styled } from '@mui/material';
import clsx from 'clsx';
import { getStyle, scrollViewClasses } from './style';
import { useDOM } from '../../hooks/useDOM';
import { getThemeToken } from './theme';
import { handleDrag } from '../../Shared/dragHandler';
import { scrollContainer } from '../../Shared/scroll';
const horizontalPropertyNames = {
    scrollPositionName: 'scrollLeft',
    scrollRangeName: 'scrollWidth',
    clientRangeName: 'clientWidth',
    positionStyleName: 'left',
    rangeStyleName: 'width',
    cornerStyleName: 'right',
};
const verticalPropertyNames = {
    scrollPositionName: 'scrollTop',
    scrollRangeName: 'scrollHeight',
    clientRangeName: 'clientHeight',
    positionStyleName: 'top',
    rangeStyleName: 'height',
    cornerStyleName: 'bottom',
};
const ScrollViewBase = React.forwardRef((props, ref) => {
    const { className, children, onScrollEnd, scrollerProps, onScroll, orientation = 'both', theme, ...otherProps } = props;
    const trackWidth = getThemeToken(theme).track.width;
    const horizontalAllowed = orientation !== 'vertical';
    const verticalAllowed = orientation !== 'horizontal';
    const horizontalAllowedRef = useRef(horizontalAllowed);
    const verticalAllowedRef = useRef(verticalAllowed);
    useLayoutEffect(() => {
        horizontalAllowedRef.current = horizontalAllowed;
        verticalAllowedRef.current = verticalAllowed;
        update();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [horizontalAllowed, verticalAllowed]);
    const rootRef = useRef(null);
    const verticalThumbRef = useRef(null);
    const horizontalThumbRef = useRef(null);
    function rootMounted(e) {
        rootRef.current = e;
        if (typeof ref === 'function') {
            ref(e);
        }
        else if (ref) {
            // eslint-disable-next-line no-param-reassign
            ref.current = e;
        }
    }
    const descriptor = useMemo(() => ({
        horizontal: { scale: 1, position: 0, range: 0 },
        vertical: { scale: 1, position: 0, range: 0 },
    }), []);
    // Called once the view position may need to be updated (scrolled, resized, etc.)
    const update = useCallback(() => {
        const scroller = scrollerRef.current;
        // istanbul ignore next
        if (!scroller) {
            return;
        }
        scroller.style.setProperty('--scroller-width', `${scroller.clientWidth}px`);
        scroller.style.setProperty('--scroller-height', `${scroller.clientHeight}px`);
        function getDirectionScrollable(isHorizontal) {
            const { scrollRangeName, clientRangeName } = isHorizontal
                ? horizontalPropertyNames
                : verticalPropertyNames;
            const { [scrollRangeName]: scrollRange, [clientRangeName]: clientRange, } = scroller;
            if (scrollRange <= clientRange) {
                return false;
            }
            return true;
        }
        const horizontalScrollable = horizontalAllowedRef.current && getDirectionScrollable(true);
        const verticalScrollable = verticalAllowedRef.current && getDirectionScrollable(false);
        function recalculateDirection(isHorizontal) {
            const thumb = (isHorizontal ? horizontalThumbRef : verticalThumbRef)
                .current;
            // istanbul ignore next
            if (!thumb) {
                return;
            }
            const track = thumb.parentElement;
            const directionScrollable = isHorizontal
                ? horizontalScrollable
                : verticalScrollable;
            if (!directionScrollable) {
                track.style.display = 'none';
                return;
            }
            const { scrollPositionName, scrollRangeName, clientRangeName, positionStyleName, rangeStyleName, cornerStyleName, } = isHorizontal ? horizontalPropertyNames : verticalPropertyNames;
            const { [scrollPositionName]: scrollPosition, [scrollRangeName]: scrollRange, [clientRangeName]: clientRange, } = scroller;
            const oppositeScrollable = isHorizontal
                ? verticalScrollable
                : horizontalScrollable;
            // We spare a space for the "corner" at the cross of axis if both axis is scrollable
            const corner = oppositeScrollable ? trackWidth : 0;
            // The gap between the edge of the track and the edge of the thumb
            const gap = Math.min(3, (clientRange - corner) / 4);
            // the range size of the track
            const trackRange = clientRange - gap * 2 - corner;
            // the size of the thumb
            const range = Math.max(Math.min(trackRange / 2, 16), (clientRange / scrollRange) * trackRange);
            thumb.style[rangeStyleName] = `${range}px`;
            const scale = (trackRange - range) / (scrollRange - clientRange);
            const position = scale * scrollPosition + gap;
            thumb.style[positionStyleName] = `${position}px`;
            track.style[cornerStyleName] = `${corner}px`;
            track.style.display = 'block';
            descriptor[isHorizontal ? 'horizontal' : 'vertical'] = {
                scale,
                position,
                range,
            };
        }
        if (horizontalAllowedRef.current) {
            recalculateDirection(true);
        }
        if (verticalAllowedRef.current) {
            recalculateDirection(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const resizeObserver = useMemo(() => {
        // istanbul ignore else
        if (window.ResizeObserver) {
            return new window.ResizeObserver(update);
        }
        // The trick is for jest tests only, since ResizeObserver gets high compatibility
        // istanbul ignore next
        return undefined;
    }, [update]);
    const registerObserver = useCallback((e) => {
        resizeObserver?.observe(e);
        return () => {
            resizeObserver?.unobserve(e);
        };
    }, [resizeObserver]);
    const [scrollerMounted, scrollerRef] = useDOM((e) => {
        const scrollerPropsRef = scrollerProps?.ref;
        const refFunc = typeof scrollerPropsRef === 'function'
            ? scrollerPropsRef
            : scrollerPropsRef &&
                ((r) => {
                    scrollerPropsRef.current = r;
                });
        refFunc?.(e);
        const unregister = registerObserver(e);
        return () => {
            unregister();
            refFunc?.(null);
        };
    }, []);
    const [contentMounted] = useDOM(registerObserver, []);
    function handleScroll(e) {
        update();
        onScroll?.(e);
        scrollerProps?.onScroll?.(e);
        if (onScrollEnd) {
            const scroller = e.currentTarget;
            if (scroller.scrollTop &&
                scroller.scrollTop >= scroller.scrollHeight - scroller.clientHeight) {
                onScrollEnd('vertical');
            }
            if (scroller.scrollLeft &&
                scroller.scrollLeft >= scroller.scrollWidth - scroller.clientWidth) {
                onScrollEnd('horizontal');
            }
        }
    }
    function registerTrackWheel(element) {
        const wheelHandler = (e) => {
            const scroller = scrollerRef.current;
            // istanbul ignore next
            if (!scroller) {
                return;
            }
            e.preventDefault();
            let { deltaX, deltaY } = e;
            // We don't want to scroll vertically and horizontally at the same time,
            // which making one-direction scroll unstable with a track pad or something.
            if (Math.abs(deltaX) > Math.abs(deltaY)) {
                deltaY = 0;
            }
            else {
                deltaX = 0;
            }
            scroller.scrollBy(deltaX, deltaY);
        };
        element.addEventListener('wheel', wheelHandler, { passive: false });
        return () => {
            element.removeEventListener('wheel', wheelHandler);
        };
    }
    // This helps when the user grab the wheel in the track (rather than in the scroller)
    const [verticalTrackMounted] = useDOM(registerTrackWheel, []);
    const [horizontalTrackMounted] = useDOM(registerTrackWheel, []);
    function renderTrack(isHorizontal) {
        // Handle user dragging the thumb
        function handleThumbMouseDown(e) {
            const root = rootRef.current;
            const scroller = scrollerRef.current;
            const thumb = e.currentTarget;
            const track = thumb.parentElement;
            // istanbul ignore next
            if (!root || !scroller || !thumb || !track) {
                return;
            }
            const { scrollPositionName } = isHorizontal
                ? horizontalPropertyNames
                : verticalPropertyNames;
            const initial = scroller[scrollPositionName];
            [root, thumb, track].forEach((ele) => {
                ele.classList.add(scrollViewClasses.active);
            });
            handleDrag(e, {
                threshold: 0,
                onMove({ deltaX, deltaY }) {
                    const delta = isHorizontal ? deltaX : deltaY;
                    const { scale } = isHorizontal
                        ? descriptor.horizontal
                        : descriptor.vertical;
                    scroller[scrollPositionName] = initial + delta / scale;
                },
                onEnd() {
                    [root, thumb, track].forEach((ele) => {
                        ele.classList.remove(scrollViewClasses.active);
                    });
                },
            });
        }
        // Handle user clicking on the track, which will scroll the scroller by one page
        function handleTrackMouseDown(e) {
            if (e.target !== e.currentTarget) {
                return;
            }
            const scroller = scrollerRef.current;
            // istanbul ignore next
            if (!scroller) {
                return;
            }
            const { position, range, scale } = isHorizontal
                ? descriptor.horizontal
                : descriptor.vertical;
            const rect = e.currentTarget.getBoundingClientRect();
            const delta = (isHorizontal ? e.clientX - rect.left : e.clientY - rect.top) >
                position + range / 2
                ? 1
                : -1;
            const length = range / scale;
            void scrollContainer(scroller, {
                [isHorizontal ? 'deltaX' : 'deltaY']: delta * Math.max(length * 0.8, length - 32),
                allowUserInterrupt: true,
                animated: true,
                animationDuration: 100,
            }).catch(/* istanbul ignore next */ () => { });
        }
        const directionalClassName = isHorizontal
            ? scrollViewClasses.horizontal
            : scrollViewClasses.vertical;
        return (React.createElement("div", { className: clsx(scrollViewClasses.track, directionalClassName), "aria-hidden": true, ref: isHorizontal ? horizontalTrackMounted : verticalTrackMounted, key: isHorizontal ? 'horizontal' : 'vertical', onMouseDown: handleTrackMouseDown },
            React.createElement("div", { className: clsx(scrollViewClasses.thumb, directionalClassName), ref: isHorizontal ? horizontalThumbRef : verticalThumbRef, onMouseDown: handleThumbMouseDown })));
    }
    return (React.createElement(Box, { ...otherProps, className: clsx(className, scrollViewClasses.root), ref: rootMounted },
        React.createElement(Box, { ...scrollerProps, className: clsx(scrollerProps?.className, scrollViewClasses.scroller, {
                [scrollViewClasses.horizontal]: horizontalAllowed,
                [scrollViewClasses.vertical]: verticalAllowed,
            }), onScroll: handleScroll, ref: scrollerMounted },
            React.createElement("div", { className: scrollViewClasses.content, ref: contentMounted }, children)),
        horizontalAllowed ? renderTrack(true) : null,
        verticalAllowed ? renderTrack(false) : null));
});
const ScrollView = styled(ScrollViewBase)((props) => {
    const { theme } = props;
    return getStyle(theme);
});
export default ScrollView;
